import React, { useEffect } from 'react'
import { Form, Input, message } from 'antd'
import { Modal } from '~/Components/Modal'
import { UserItemData } from '~/Services/UserService.d'
import { useAppSelector } from '~/Store/hooks'
import { getDefaultOrganization } from '~/Store/User/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { UsersActions } from '~/Store/AdminUser/slice'
import { RootState } from '~/Store/reducers'

interface EditUserModalProps {
  visible: boolean
  user: UserItemData | null
  onClose: () => void
  onSuccess?: () => void
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  visible,
  user,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const updateStatus = useSelector(
    (state: RootState) => state.users.updateStatus,
  )
  const updateMessage = useSelector(
    (state: RootState) => state.users.updateMessage,
  )

  const updating = updateStatus === 'updating'

  const defaultOrg = useAppSelector(getDefaultOrganization)

  useEffect(() => {
    if (visible && user) {
      form.setFieldsValue({
        firstName: user.firstname || '',
        lastName: user.lastname || '',
        email: user.email || '',
        password: '******',
        confirmPassword: '******',
      })
    } else {
      form.resetFields()
    }
  }, [visible, user, form])

  useEffect(() => {
    if (!visible) return

    if (updateStatus === 'success') {
      message.success('Cập nhật thông tin user thành công!')
      dispatch(UsersActions.resetUpdateUser())
      form.resetFields()
      onClose()
    }

    if (updateStatus === 'fail') {
      message.error(updateMessage || 'Cập nhật thất bại')
      dispatch(UsersActions.resetUpdateUser())
    }
  }, [updateStatus, updateMessage, visible])

  const handleSubmit = async (values: any) => {
    if (!user || !defaultOrg) {
      message.error('Không lấy được thông tin user hoặc tổ chức!')
      return
    }

    if (values.password && values.password !== values.confirmPassword) {
      message.error('Mật khẩu xác nhận không khớp!')
      return
    }

    const updatedUser: UserItemData = {
      ...user,
      firstname: values.firstName,
      lastname: values.lastName,
      ...(values.password ? { password: values.password } : {}),
    }

    dispatch(UsersActions.updateUsersRequest(updatedUser))
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title="Update user infor"
      visible={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      confirmLoading={updating}
      width={500}
      okText="Lưu thay đổi"
      cancelText="Hủy"
      loadingData={updating}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Tên"
          name="firstName"
          rules={[
            { required: true, message: 'Vui lòng nhập tên!' },
            { max: 50, message: 'Tên không quá 50 ký tự!' },
          ]}
        >
          <Input placeholder="Nhập tên" />
        </Form.Item>

        <Form.Item
          label="Họ và Tên đệm"
          name="lastName"
          rules={[
            { required: true, message: 'Vui lòng nhập họ và tên đệm!' },
            { max: 100, message: 'Họ và tên đệm không quá 100 ký tự!' },
          ]}
        >
          <Input placeholder="Nhập họ và tên đệm" />
        </Form.Item>

        <Form.Item
          label="Mật khẩu"
          name="password"
          rules={[{ min: 6, message: 'Mật khẩu tối thiểu 6 ký tự!' }]}
        >
          <Input.Password placeholder="Nhập mật khẩu mới" />
        </Form.Item>

        <Form.Item
          label="Xác nhận mật khẩu"
          name="confirmPassword"
          dependencies={['password']}
          hasFeedback
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const password = getFieldValue('password')
                if (!password || !value || password === value) {
                  return Promise.resolve()
                }
                return Promise.reject(
                  new Error('Mật khẩu xác nhận phải trùng với mật khẩu!'),
                )
              },
            }),
          ]}
        >
          <Input.Password placeholder="Nhập lại mật khẩu" />
        </Form.Item>

        <Form.Item
          label="Địa chỉ email"
          name="email"
          rules={[
            { type: 'email', message: 'Email không đúng định dạng' },
            { required: true, message: 'Email là bắt buộc' },
          ]}
        >
          <Input disabled placeholder="Email không thể thay đổi" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
